<template>
  <div v-if="itemLoaded && routeDataLoaded" class="admin-community">
    <b-row>
      <b-col>
        <h1 v-if="item.name">{{ item.name }}</h1>
        <h1 v-else>
          <em>{{ $tc("communities.community", 1) | capitalize }}</em>
        </h1>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <validation-observer ref="observer" v-slot="{ passes }">
          <b-form class="form" @submit.prevent="passes(submit)">
            <div v-if="item.id" class="form__section">
              <div v-if="canAddCommunityFriend || item.friends_count > 0">
                <a id="friends" />
                <community-friends-list
                  :id="`admin-community-friends-table-${item.id}`"
                  ref="friendsTable"
                  class="mb-3"
                  :hide-fields="['community.name', 'community_id']"
                  :fetch-params="{ community_id: item.id, for: 'admin' }"
                  :label="$t('views.admin.community.friends.title')"
                >
                  <template #head-buttons>
                    <relation-input
                      v-if="canAddCommunityFriend"
                      :placeholder="$t('views.admin.community.friends.add')"
                      class="add-friend-community-input"
                      :value="null"
                      reset-after-select
                      :query="{
                        slug: 'communities',
                        value: 'id',
                        text: 'name',
                        params: {
                          '!communities.id': item.id,
                        },
                      }"
                      name="add-community-friend"
                      @input="addCommunityFriend"
                    />
                  </template>
                </community-friends-list>

                <div class="text-secondary small mt-1">
                  {{ $t("views.admin.community.friends.description") }}
                </div>
              </div>
            </div>

            <form-section
              v-if="item.id && canManageRoles"
              id="roles"
              :section-title="$t('views.admin.community.roles.title')"
              toggleable
            >
              <community-user-roles-list
                :id="`admin-community-roles-table-${item.id}`"
                ref="rolesTable"
                class="mb-3"
                :community="item"
              >
              </community-user-roles-list>
            </form-section>

            <div v-if="item.id" class="form__section">
              <a id="members" />
              <community-users-list
                :id="`admin-community-users-table-${item.id}`"
                ref="userTable"
                class="mb-3"
                :hide-fields="['community.name', 'community_id', 'updated_at']"
                :fetch-params="{ community_id: item.id, for: 'admin' }"
                :community-id="item.id"
                :label="$t('views.admin.community.members.title')"
                show-mailbox-export
              >
                <template #head-buttons>
                  <relation-input
                    v-if="canAddCommunityUser"
                    :placeholder="$t('views.admin.community.members.add')"
                    class="add-user-input"
                    :value="null"
                    reset-after-select
                    :query="{
                      slug: 'users',
                      value: 'id',
                      text: 'full_name',
                      params: {
                        '!communities.id': item.id,
                      },
                      details: 'email',
                    }"
                    name="add-member"
                    @input="addUser"
                  />
                </template>
              </community-users-list>
            </div>

            <div v-if="item.id" class="form__section">
              <h2>{{ $t("views.admin.community.mailing_lists.title") }}</h2>

              <b-card
                v-for="community_mailing_list in item.community_mailing_lists"
                :key="community_mailing_list.id"
                class="community-mailing-list"
                :class="{ 'has-error': community_mailing_list.consecutive_error_count > 0 }"
              >
                <div class="community-mailing-list-details">
                  <div>
                    <div>
                      <div class="community-mailing-list-detail-header">
                        {{ $t("views.admin.community.mailing_list.provider") }}
                      </div>
                      <span class="community-mailing-list-detail">
                        {{ community_mailing_list.provider | capitalize }}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div class="community-mailing-list-detail-header">
                      {{ $t("views.admin.community.mailing_list.list") }}
                    </div>
                    <span class="community-mailing-list-detail">
                      {{ community_mailing_list.list_name }}
                    </span>
                  </div>
                  <div v-if="community_mailing_list.tag">
                    <div class="community-mailing-list-detail-header">
                      {{ $t("views.admin.community.mailing_list.tag") }}
                    </div>
                    <span class="community-mailing-list-detail">
                      {{ community_mailing_list.tag }}
                    </span>
                  </div>
                  <div v-if="community_mailing_list.created_by_user">
                    <div class="community-mailing-list-detail-header">
                      {{ $t("views.admin.community.mailing_list.created_by") }}
                    </div>
                    <span class="community-mailing-list-detail">
                      <b-link :to="`/admin/users/${community_mailing_list.created_by_user.id}`">
                        {{ community_mailing_list.created_by_user.full_name }}
                      </b-link>
                    </span>
                  </div>
                  <div>
                    <div class="community-mailing-list-detail-header">
                      {{ $t("views.admin.community.mailing_list.status.title") }}
                    </div>
                    <b-badge
                      v-if="community_mailing_list.status === 'active'"
                      class="community-mailing-list-status"
                      variant="success"
                    >
                      <b-icon icon="check-circle" />
                      {{ $t("views.admin.community.mailing_list.status.active") }}
                    </b-badge>
                    <b-badge
                      v-else-if="community_mailing_list.status === 'suspended'"
                      class="community-mailing-list-status"
                      variant="danger"
                    >
                      <b-icon icon="pause-circle" />
                      {{ $t("views.admin.community.mailing_list.status.suspended") }}
                    </b-badge>
                    <b-badge
                      v-else-if="community_mailing_list.status === 'synchronizing'"
                      class="community-mailing-list-status"
                      variant="warning"
                    >
                      <b-icon icon="arrow-repeat" />
                      {{ $t("views.admin.community.mailing_list.status.synchronizing") }}
                    </b-badge>
                  </div>

                  <b-alert
                    v-if="community_mailing_list.consecutive_error_count > 0"
                    variant="warning"
                    class="community-mailing-list-error"
                    show
                  >
                    <strong>{{
                      $t("views.admin.community.mailing_list.error.title", {
                        count: community_mailing_list.consecutive_error_count,
                      })
                    }}</strong>
                    <br />
                    <b-link v-b-modal="'last-mailing-list-error-modal'">{{
                      $t("views.admin.community.mailing_list.error.view_error")
                    }}</b-link>
                  </b-alert>
                  <b-modal
                    id="last-mailing-list-error-modal"
                    hide-footer
                    hide-header
                    size="lg"
                    centered
                  >
                    <h4>{{ $t("views.admin.community.mailing_list.error.modal_title") }}</h4>
                    <pre class="text-monospace">{{ community_mailing_list.last_error }}</pre>
                    <icon-button
                      icon="files"
                      class="mt-2"
                      :onclick="() => copyLastMailingListError(community_mailing_list.id)"
                      >{{ $t("forms.copy") | capitalize }}</icon-button
                    >
                  </b-modal>
                </div>
                <div class="community-mailing-list-buttons">
                  <icon-button
                    size="sm"
                    role="delete"
                    :onclick="() => deleteCommunityMailingList(community_mailing_list.id)"
                    >{{ $t("forms.delete") | capitalize }}</icon-button
                  >
                </div>
              </b-card>
              <icon-button v-b-modal="'add-community-mailing-list-modal'" role="add" class="mb-3">{{
                $t("forms.add") | capitalize
              }}</icon-button>
              <b-modal id="add-community-mailing-list-modal" hide-header hide-footer>
                <mailing-list-form
                  :community-id="item.id"
                  @added="
                    (mailingList) => {
                      item.community_mailing_lists.push(mailingList);
                      $bvModal.hide('add-community-mailing-list-modal');
                    }
                  "
                />
              </b-modal>

              <p v-if="isGlobalAdmin">
                {{ $t("views.admin.community.mailing_list.admin_note") }}
                <b-link to="/admin/mailinglists">{{
                  $t("views.admin.community.mailing_list.admin_link")
                }}</b-link>
              </p>
            </div>

            <div v-if="item.id" class="form__section">
              <h2>{{ $t("views.admin.community.invite.title") }}</h2>
              <b-input-group>
                <multiple-email-input
                  v-model="inviteEmails"
                  :placeholder="$t('views.admin.community.invite.placeholder')"
                  class="flex-grow-1"
                  @submit="redirectToNewInvitation"
                />
                <b-input-group-append>
                  <icon-button
                    variant="outline-success"
                    icon="arrow-right-square"
                    @click="redirectToNewInvitation"
                  >
                  </icon-button>
                </b-input-group-append>
              </b-input-group>
            </div>

            <div class="form__section">
              <h2>{{ $t("views.admin.community.general.title") }}</h2>

              <conditional-context
                :show="!canEditCommunityConfig"
                :label="$t('views.admin.community.general.admin_only')"
              >
                <forms-builder
                  v-model="item"
                  entity="communities"
                  :definition="form"
                  :fields="['name', 'type']"
                  :disabled="!canEditCommunityConfig"
                />
              </conditional-context>

              <forms-builder
                v-model="item"
                :definition="form"
                :fields="['starting_guide_url', 'chat_group_url', 'contact_email', 'description']"
                entity="communities"
              />

              <b-form-group
                :label="$t('views.admin.community.general.welcome_email.title')"
                :description="$t('views.admin.community.general.welcome_email.description')"
              >
                <markdown-editor
                  v-model="item.custom_registration_approved_email"
                  :send-sample="sendSampleApprovedEmail"
                  :show-preview-initially="!!item.custom_registration_approved_email"
                />
              </b-form-group>

              <b-form-group
                :label="$t('views.admin.community.general.incorrect_proof_email.title')"
                :description="$t('views.admin.community.general.incorrect_proof_email.description')"
              >
                <markdown-editor
                  v-model="item.custom_incorrect_proof_email"
                  allow-empty-sample
                  :placeholder="item.default_incorrect_proof_email"
                  :send-sample="sendSampleIncorrectProofEmail"
                  :show-preview-initially="!!item.custom_incorrect_proof_email"
                />
              </b-form-group>

              <h2>{{ $t("views.admin.community.sharing.title") }}</h2>

              <conditional-context
                :show="!canEditCommunityConfig"
                :label="$t('views.admin.community.general.admin_only')"
              >
                <b-form-group
                  :label="$t('views.admin.community.sharing.vehicle_types')"
                  label-for="area"
                >
                  <b-form-checkbox-group
                    :disabled="!canEditCommunityConfig"
                    :checked="(item.allowed_loanable_types || []).map((l) => l.id)"
                    name="allowed_loanable_types"
                    :options="allLoanableTypes"
                    @change="changeLoanableTypes"
                  />
                </b-form-group>

                <forms-builder
                  v-model="item"
                  :definition="form"
                  :disabled="!canEditCommunityConfig"
                  :disabled-reason="$t('views.admin.community.permissions.global_admins_only')"
                  :fields="['exempt_from_contributions', 'members_can_add_loanables']"
                  entity="communities"
                />
              </conditional-context>

              <b-form-group :label="$tc('communities.fields.required_proofs.title', 2)">
                <b-checkbox
                  v-model="item.requires_residency_proof"
                  inline
                  name="requires_residency_proof"
                >
                  {{ $t("communities.fields.required_proofs.options.residency") }}
                </b-checkbox>
                <b-checkbox
                  v-model="item.requires_identity_proof"
                  inline
                  name="requires_identity_proof"
                >
                  {{ $t("communities.fields.required_proofs.options.identity") }}
                </b-checkbox>
                <b-checkbox
                  v-model="item.requires_custom_proof"
                  inline
                  name="requires_custom_proof"
                >
                  {{ $t("communities.fields.required_proofs.options.custom") }}
                </b-checkbox>
              </b-form-group>
              <conditional-context
                v-if="item.requires_custom_proof"
                show
                :label="$t('communities.fields.custom_proof') | capitalize"
              >
                <forms-builder
                  v-model="item"
                  :definition="form"
                  :fields="['custom_proof_name']"
                  entity="communities"
                />
                <b-form-group :label="$t('communities.fields.custom_proof_desc')">
                  <markdown-editor
                    v-model="item.custom_proof_desc"
                    :show-preview-initially="!!item.custom_proof_desc"
                  />
                </b-form-group>
              </conditional-context>
            </div>

            <form-section
              :inititally-visible="!item.id"
              :section-title="$t('communities.fields.geographical_area.title') | capitalize"
              toggleable
            >
              <conditional-context
                :show="!canEditCommunityConfig"
                :label="$t('views.admin.community.permissions.global_admins_only')"
              >
                <simple-map class="community-map" :polygons="communityPolygons" />

                <b-form-group
                  :description="$t('communities.fields.geographical_area.description')"
                  label-for="area"
                >
                  <b-form-textarea
                    id="area"
                    v-model="area"
                    class="monospace"
                    :disabled="!canEditCommunityConfig"
                    name="area"
                    rows="6"
                    max-rows="12"
                  />
                </b-form-group>
              </conditional-context>
            </form-section>
            <form-buttons :changed="changed" :saving="loading" @reset="reset" />

            <pricing-table
              v-if="item.id"
              :community-id="item.id"
              :available-loanable-types="availableLoanableTypes"
              :exempt-from-contributions="item.exempt_from_contributions"
              :is-private-community="item.type === 'private'"
            />
          </b-form>
        </validation-observer>
      </b-col>
    </b-row>
  </div>
  <layout-loading v-else />
</template>

<script>
import MailingListForm from "@/components/Admin/MailingListForm.vue";
import CommunityFriendsList from "@/components/Community/CommunityFriendsList.vue";
import CommunityUsersList from "@/components/Community/CommunityUsersList.vue";
import CommunityUserRolesList from "@/components/Community/UserRolesList.vue";
import FormsBuilder from "@/components/Forms/Builder.vue";
import MultipleEmailInput from "@/components/Forms/MultipleEmailInput.vue";
import RelationInput from "@/components/Forms/RelationInput.vue";
import FormSection from "@/components/Loanable/FormSection.vue";
import SimpleMap from "@/components/Loanable/SimpleMap.vue";
import PricingTable from "@/components/Pricing/PricingTable.vue";
import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import FormButtons from "@/components/shared/FormButtons.vue";
import IconButton from "@/components/shared/IconButton.vue";
import MarkdownEditor from "@/components/shared/MarkdownEditor.vue";
import { capitalize } from "@/helpers/filters";
import { extractMultipolygonPaths } from "@/helpers/geoJson";
import { canManageCommunityRoles } from "@/helpers/permissions/communities";
import { isGlobalAdmin } from "@/helpers/permissions/users";

import DataRouteGuards from "@/mixins/DataRouteGuards";

import FormMixin from "@/mixins/FormMixin";
import { del, put } from "@/requests/server";
import { urlencode } from "locutus/php/url";
import is from "vue2-datepicker/locale/es/is";

export default {
  name: "AdminCommunity",
  components: {
    MarkdownEditor,
    MailingListForm,
    IconButton,
    PricingTable,
    FormButtons,
    RelationInput,
    MultipleEmailInput,
    ConditionalContext,
    FormSection,
    CommunityFriendsList,
    CommunityUsersList,
    CommunityUserRolesList,
    FormsBuilder,
    SimpleMap,
  },
  mixins: [DataRouteGuards, FormMixin],
  data() {
    return {
      inviteEmails: "",
      mapOptions: {
        clickableIcons: false,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        maxZoom: 18,
        styles: [
          {
            featureType: "poi",
            stylers: [{ visibility: "off" }],
          },
        ],
      },
      polygonOptions: {
        fillColor: "#16a59e",
        fillOpacity: 0.25,
        strokeOpacity: 0,
        zIndex: 2,
      },
      ignoreFields: ["pricings", "community_mailing_list"],
    };
  },
  computed: {
    is() {
      return is;
    },
    canEditCommunityConfig() {
      return isGlobalAdmin(this.$store.state.user);
    },
    canAddCommunityFriend() {
      return isGlobalAdmin(this.$store.state.user);
    },
    canAddCommunityUser() {
      return isGlobalAdmin(this.$store.state.user);
    },
    canManageRoles() {
      return canManageCommunityRoles(this.$store.state.user, this.item);
    },
    // For form mixin, only keeps these fields in API requests
    requestFieldsAllowlist() {
      if (this.canEditCommunityConfig) {
        return [
          "id",
          "chat_group_url",
          "description",
          "contact_email",
          "requires_identity_proof",
          "requires_residency_proof",
          "requires_custom_proof",
          "custom_proof_name",
          "custom_proof_desc",
          "starting_guide_url",
          "pricings",
          "name",
          "type",
          "exempt_from_contributions",
          "allowed_loanable_types",
          "area",
          "custom_registration_approved_email",
          "custom_incorrect_proof_email",
          "members_can_add_loanables",
        ];
      }
      let fields = [
        "id",
        "chat_group_url",
        "description",
        "contact_email",
        "requires_identity_proof",
        "requires_residency_proof",
        "requires_custom_proof",
        "custom_proof_name",
        "custom_proof_desc",
        "starting_guide_url",
        "pricings",
        "custom_registration_approved_email",
        "custom_incorrect_proof_email",
      ];
      return fields;
    },
    area: {
      get() {
        return this.item.area ? JSON.stringify(this.item?.area) : "";
      },
      set(value) {
        try {
          this.item.area = value && value !== "" ? JSON.parse(value) : null;
        } catch (e) {
          // do nothing if value is not valid JSON. This means the form-input data will only
          // be saved to the js object when it is valid json.
        }
      },
    },
    communityPolygons() {
      if (!this.item?.area?.coordinates) {
        return [];
      }

      return extractMultipolygonPaths(this.item?.area);
    },
    allLoanableTypes() {
      return this.$store.state.loanableTypes.types.map((t) => ({
        text: capitalize(this.$t(`loanables.fields.types.${t.name}`)),
        value: t.id,
      }));
    },
    availableLoanableTypes() {
      return this.$store.state.loanableTypes.types
        .filter((t) => !!this.item?.allowed_loanable_types?.find((a) => a.id === t.id))
        .map((t) => t.name);
    },
    isGlobalAdmin() {
      return isGlobalAdmin(this.$store.state.user);
    },
  },
  methods: {
    redirectToNewInvitation() {
      this.$router.push(
        `/admin/invitations/new?community_id=${this.item.id}&emails=${urlencode(this.inviteEmails)}`
      );
    },
    async deleteCommunityMailingList(id) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        this.$t("views.admin.community.mailing_lists.confirm_delete"),
        {
          centered: true,
          okTitle: capitalize(this.$t("forms.delete")),
          cancelTitle: capitalize(this.$t("forms.cancel")),
          okVariant: "danger",
          cancelVariant: "ghost-secondary",
        }
      );
      if (!confirm) {
        return;
      }
      await del(`/mailinglists/${id}`, {
        notifications: {
          action: this.$t("views.admin.community.notifications.delete_integration.action"),
          onSuccess: this.$t("views.admin.community.notifications.delete_integration.success"),
        },
      });

      this.item.community_mailing_lists = this.item.community_mailing_lists.filter(
        (list) => list.id !== id
      );
    },
    async addCommunityFriend(community) {
      if (!community) {
        return;
      }

      await this.$store.dispatch(`communities/addFriend`, {
        friendId: community.id,
        communityId: this.item.id,
      });

      this.$refs.friendsTable.refresh();
    },
    async addUser(user) {
      if (!user) {
        return;
      }

      await this.$store.dispatch(`communities/addUser`, {
        userId: user.id,
        communityId: this.item.id,
      });
      this.$refs.userTable.setFilters({ user_id: user.id });
    },
    changeLoanableTypes(types) {
      this.$store.commit(`${this.slug}/item`, {
        ...this.item,
        allowed_loanable_types: types.map((t) => ({ id: t })),
      });
    },
    copyLastMailingListError(id) {
      navigator.clipboard
        .writeText(this.item.community_mailing_lists.find((list) => list.id === id).last_error)
        .then(() => {
          this.$store.commit("addNotification", {
            title: this.$t("views.admin.community.notifications.copy_error_message.success"),
            variant: "success",
          });
        });
    },
    async sendSampleApprovedEmail() {
      await put(
        `/communities/${this.item.id}/approve/sample`,
        {
          markdown: this.item.custom_registration_approved_email,
        },
        {
          notifications: {
            action: this.$t("views.admin.community.notifications.send_sample.action"),
            onSuccess: true,
          },
        }
      );
    },
    async sendSampleIncorrectProofEmail() {
      await put(
        `/communities/${this.item.id}/incorrect-proof/sample`,
        {
          markdown: this.item.custom_incorrect_proof_email,
        },
        {
          notifications: {
            action: this.$t(
              "views.admin.community.notifications.send_incorrect_proof_sample.action"
            ),
            onSuccess: true,
          },
        }
      );
    },
  },
};
</script>

<style lang="scss">
@import "~bootstrap/scss/mixins/breakpoints";
.admin-community {
  .simple-map {
    width: 100%;
    height: 20rem;
  }

  .add-friend-community-input {
    min-width: 15rem;
  }
  .add-user-input {
    min-width: 15rem;
  }

  .community-mailing-list {
    &.has-error {
      .community-mailing-list-buttons {
        align-self: flex-start;
      }
    }
    &.card {
      box-shadow: $small-shadow;
      margin-bottom: 1rem;
    }

    .card-body {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      @include media-breakpoint-down(xs) {
        align-items: flex-start;
        flex-direction: column;
      }
      .community-mailing-list-details {
        display: grid;
        gap: 1rem;
        grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
        flex: 1;
        width: 100%;
      }
      .community-mailing-list-error {
        grid-column: 1 / -1; // From first to last
        margin-bottom: 0;
      }

      .community-mailing-list-provider {
        font-size: 1.125rem;
        font-weight: 700;
        color: $primary;
      }
      .community-mailing-list-detail-header {
        font-size: 0.875rem;
        color: $content-neutral-secondary;
      }
      .community-mailing-list-detail {
        font-weight: 600;
      }
    }
  }
}
</style>
